/**
 * FacultyDashboardPage - Faculty portal main dashboard
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FacultyLayout from './FacultyLayout';
import {
  getFacultyDashboard,
  respondToAssignment,
  formatCurrency,
  getOnboardingProgress,
  isAssignmentOverdue,
  getHoursRemaining,
  type FacultyDashboardData,
  type FacultyPendingAssignment,
} from '../../api/faculty';
import { formatTimeDisplay } from '@sda/shared';

export default function FacultyDashboardPage() {
  const [dashboard, setDashboard] = useState<FacultyDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getFacultyDashboard();
      setDashboard(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignmentResponse = async (
    classId: string,
    response: 'accept' | 'decline'
  ) => {
    try {
      setRespondingTo(classId);
      setError(null);
      await respondToAssignment(classId, response);
      await loadDashboard();
    } catch (err) {
      console.error('Assignment response error:', err);
      const raw = err instanceof Error ? err.message : 'Failed to respond to assignment';
      // Map known errors to friendly messages
      const friendlyMessages: Record<string, string> = {
        'Assignment already responded to': 'This assignment has already been responded to.',
        'Assignment not found': 'This assignment is no longer available.',
      };
      // Reload dashboard to refresh the assignments list first, then set the
      // error — loadDashboard() clears the error state on entry, so setting
      // it beforehand would get wiped before it ever renders.
      await loadDashboard();
      setError(friendlyMessages[raw] || raw);
    } finally {
      setRespondingTo(null);
    }
  };

  if (isLoading) {
    return (
      <FacultyLayout>
        <div className="space-y-6">
          <div className="h-8 bg-warm-100 rounded w-1/3 animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-4 bg-warm-100 rounded w-1/2 mb-2" />
                <div className="h-8 bg-warm-100 rounded w-2/3" />
              </div>
            ))}
          </div>
        </div>
      </FacultyLayout>
    );
  }

  if (!dashboard) {
    return (
      <FacultyLayout>
        <div className="card text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-danger-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-warm-900 mb-2">Failed to Load Dashboard</h3>
          <p className="text-warm-600 mb-4">{error}</p>
          <button onClick={loadDashboard} className="btn-primary">
            Try Again
          </button>
        </div>
      </FacultyLayout>
    );
  }

  const { profile, stats, upcoming_classes, pending_assignments } = dashboard;
  const onboardingPercent = getOnboardingProgress(profile.onboarding_progress);
  // Only check percentage - status may still be 'onboarding' until admin approves
  const needsOnboarding = onboardingPercent < 100;

  return (
    <FacultyLayout>
      {/* Welcome & Onboarding Banner */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900 mb-2">
          Welcome back, {profile.first_name}!
        </h1>
        <p className="text-warm-600 hidden sm:block">
          {needsOnboarding
            ? 'Complete your onboarding to start receiving class assignments.'
            : 'Here\'s an overview of your upcoming classes and assignments.'}
        </p>
      </div>

      {/* Onboarding Progress Banner */}
      {needsOnboarding && (
        <Link
          to="/faculty/onboarding"
          className="card mb-8 bg-gradient-to-r from-primary-50 to-primary-50 border-primary-200 hover:shadow-md transition-shadow block"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-warm-900 mb-1">
                Complete Your Profile
              </h2>
              <p className="text-warm-600 text-sm">
                You're {onboardingPercent}% done. Finish setting up to receive class assignments.
              </p>
              <div className="mt-3 h-2 bg-white rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all duration-500"
                  style={{ width: `${onboardingPercent}%` }}
                />
              </div>
            </div>
            <div className="flex items-center gap-2 text-primary-600">
              <span className="font-medium">Continue Setup</span>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </Link>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="card">
          <p className="text-sm text-warm-500 mb-1">Total Classes</p>
          <p className="text-2xl font-bold text-warm-900">{stats.total_classes}</p>
        </div>
        <div className="card">
          <p className="text-sm text-warm-500 mb-1">Upcoming</p>
          <p className="text-2xl font-bold text-primary-600">{stats.upcoming_classes}</p>
        </div>
        <div className="card">
          <p className="text-sm text-warm-500 mb-1">Pending Response</p>
          <p className="text-2xl font-bold text-warning-600">{stats.pending_responses}</p>
        </div>
        <div className="card">
          <p className="text-sm text-warm-500 mb-1">Total Earnings</p>
          <p className="text-2xl font-bold text-success-600">
            {formatCurrency(stats.total_earnings_cents)}
          </p>
        </div>
      </div>

      {/* Inline Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-warning-50 border border-warning-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-warning-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span className="text-warning-800 text-sm">{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-warning-500 hover:text-warning-700 shrink-0">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Pending Assignments */}
      {pending_assignments.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-display font-bold text-warm-900">
              Pending Assignments
            </h2>
            <span className="px-2 py-1 bg-warning-100 text-warning-700 text-sm font-medium rounded-full">
              {pending_assignments.length} awaiting response
            </span>
          </div>
          <div className="space-y-4">
            {pending_assignments.map((assignment) => (
              <AssignmentCard
                key={assignment.id}
                assignment={assignment}
                onRespond={handleAssignmentResponse}
                isResponding={respondingTo === assignment.class_id}
              />
            ))}
          </div>
        </section>
      )}

      {/* Upcoming Classes */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-display font-bold text-warm-900">
            Upcoming Classes
          </h2>
          <Link to="/faculty/schedule" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
            View Schedule →
          </Link>
        </div>

        {upcoming_classes.length === 0 ? (
          <div className="card text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-warm-900 mb-2">No Upcoming Classes</h3>
            <p className="text-warm-600">
              {needsOnboarding
                ? 'Complete your profile to start receiving class assignments.'
                : 'You\'ll see your scheduled classes here once you\'re assigned.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {upcoming_classes.slice(0, 5).map((classItem) => (
              <div key={classItem.id} className="card">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Show Logo */}
                  <div className="w-14 h-14 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    {classItem.show_logo_url ? (
                      <img
                        src={classItem.show_logo_url}
                        alt={classItem.show_name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <span className="text-primary-500 text-xl font-display font-bold">
                        {classItem.show_name.charAt(0)}
                      </span>
                    )}
                  </div>

                  {/* Class Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-warm-900">{classItem.show_name}</h3>
                    <p className="text-sm text-warm-600">
                      {new Date(classItem.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                      })} • {formatTimeDisplay(classItem.start_time)}
                    </p>
                    <p className="text-sm text-warm-500">
                      {classItem.studio_name} • {classItem.studio_city}, {classItem.studio_state}
                    </p>
                  </div>

                  {/* Status */}
                  <div className="text-right">
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-success-100 text-success-700">
                      Confirmed
                    </span>
                    {classItem.student_count && (
                      <p className="text-sm text-warm-500 mt-1">
                        {classItem.student_count} students
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </FacultyLayout>
  );
}

/**
 * AssignmentCard - Pending assignment with accept/decline actions
 */
interface AssignmentCardProps {
  assignment: FacultyPendingAssignment;
  onRespond: (classId: string, response: 'accept' | 'decline') => void;
  isResponding: boolean;
}

function AssignmentCard({ assignment, onRespond, isResponding }: AssignmentCardProps) {
  const overdue = isAssignmentOverdue(assignment.response_deadline);
  const hoursRemaining = getHoursRemaining(assignment.response_deadline);
  const [confirmAction, setConfirmAction] = useState<'accept' | 'decline' | null>(null);

  return (
    <>
      <div className={`card border-l-4 ${overdue ? 'border-l-danger-500' : 'border-l-warning-500'}`}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Show Logo */}
          <div className="w-14 h-14 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
            {assignment.show_logo_url ? (
              <img
                src={assignment.show_logo_url}
                alt={assignment.show_name}
                className="w-full h-full object-cover rounded-lg"
              />
            ) : (
              <span className="text-primary-500 text-xl font-display font-bold">
                {assignment.show_name.charAt(0)}
              </span>
            )}
          </div>

          {/* Assignment Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-warm-900">{assignment.show_name}</h3>
              {overdue ? (
                <span className="px-2 py-0.5 bg-danger-100 text-danger-700 text-xs font-medium rounded-full">
                  Overdue
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-warning-100 text-warning-700 text-xs font-medium rounded-full">
                  {hoursRemaining}h remaining
                </span>
              )}
            </div>
            <p className="text-sm text-warm-600">
              {new Date(assignment.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })} • {formatTimeDisplay(assignment.start_time)}
            </p>
            <p className="text-sm text-warm-500">
              {assignment.studio_name} • {assignment.studio_city}, {assignment.studio_state}
            </p>
            {assignment.student_count && (
              <p className="text-sm text-warm-500 mt-1">
                {assignment.student_count} students • {assignment.student_level || 'Level TBD'}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setConfirmAction('decline')}
              disabled={isResponding}
              className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 transition-colors"
            >
              Decline
            </button>
            <button
              onClick={() => setConfirmAction('accept')}
              disabled={isResponding}
              className="btn-primary disabled:opacity-50"
            >
              {isResponding ? 'Processing...' : 'Accept'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-warm-900 mb-2">
              {confirmAction === 'accept' ? 'Accept Assignment?' : 'Decline Assignment?'}
            </h3>
            <p className="text-warm-600 mb-6">
              {confirmAction === 'accept'
                ? `Are you sure you want to accept the ${assignment.show_name} assignment on ${new Date(assignment.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}?`
                : `Are you sure you want to decline the ${assignment.show_name} assignment? This will open the class for reassignment to another instructor.`}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  onRespond(assignment.class_id, confirmAction);
                  setConfirmAction(null);
                }}
                className={confirmAction === 'accept'
                  ? 'btn-primary'
                  : 'px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 transition-colors font-medium'}
              >
                {confirmAction === 'accept' ? 'Yes, Accept' : 'Yes, Decline'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
